import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import ExportBar from "../../Components/Accounting/ExportBar";
import Select from "react-select";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import AppModal from "../../Components/Accounting/AppModal";

const Projects = () => {
  const navigate = useNavigate();
  const [projects,   setProjects]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showAdd,    setShowAdd]    = useState(false);
  const [showVA,     setShowVA]     = useState(false);   // budget vs actual modal
  const [selected,   setSelected]   = useState(null);
  const [vaData,     setVaData]     = useState(null);
  const [vaLoading,  setVaLoading]  = useState(false);
  const [saving,     sSaving]       = useState(false);
  const [fyList,     setFyList]     = useState([]);
  const [selectedFY, setSelectedFY] = useState(null);

  const [form, setForm] = useState({
    code:"", name:"", type: { value:"project", label:"Project" },
    description:"", start_date:"", end_date:"", budget:"", parent_id: null,
  });

  useEffect(() => { fetchProjects(); fetchFYs(); }, []);

  const fetchProjects = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listProjects({ status: "active" });
    if (res.status === "OK") setProjects(Array.isArray(res.details) ? res.details : res.details?.list || []);
    setLoading(false);
  };

  const fetchFYs = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      const list = res.details || [];
      setFyList(list);
      const cur = list.find((y) => y.is_current === 1) || list[0];
      if (cur) setSelectedFY({ value: cur.id, label: cur.name });
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name) { toast.error("Code and name are required"); return; }
    sSaving(true);
    const res = await ajaxAccounting.createProject({
      code:        form.code,
      name:        form.name,
      type:        form.type?.value || "project",
      description: form.description,
      start_date:  form.start_date || null,
      end_date:    form.end_date   || null,
      budget:      form.budget ? parseFloat(form.budget) : null,
      parent_id:   form.parent_id?.value || null,
    });
    if (res.status === "OK") {
      toast.success("Project created!");
      fetchProjects();
      setShowAdd(false);
      setForm({ code:"", name:"", type:{ value:"project", label:"Project" }, description:"", start_date:"", end_date:"", budget:"", parent_id: null });
    } else toast.error(res.message);
    sSaving(false);
  };

  const handleClose = async (id) => {
    if (!window.confirm("Close this project? It will no longer be available for new transactions.")) return;
    const res = await ajaxAccounting.closeProject({ id });
    if (res.status === "OK") { toast.success("Project closed."); fetchProjects(); }
    else toast.error(res.message);
  };

  const openVA = async (project) => {
    setSelected(project);
    setShowVA(true);
    setVaData(null);
    setVaLoading(true);
    const res = await ajaxAccounting.getProjectBudgetVsActual({ project_id: project.id, financial_year_id: selectedFY?.value });
    if (res.status === "OK") setVaData(res.details);
    else toast.error(res.message);
    setVaLoading(false);
  };

  const typeOptions    = [{ value:"project", label:"Project" }, { value:"cost_centre", label:"Cost Centre" }, { value:"department", label:"Department" }, { value:"grant", label:"Grant" }];
  const parentOptions  = projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }));
  const fyOptions      = fyList.map((y) => ({ value: y.id, label: y.name }));

  const statusBadge = (s) => <span className={`badge bg-${s === "active" ? "success" : s === "closed" ? "secondary" : "warning"}`}>{s}</span>;

  const columns = [
    { key:"code",       label:"Code",    render:(v) => <code className="fw-bold">{v}</code> },
    { key:"name",       label:"Name",    render:(v) => <strong>{v}</strong> },
    { key:"type",       label:"Type",    render:(v) => <span className="badge bg-info text-capitalize">{v?.replace("_"," ")}</span> },
    { key:"budget",     label:"Budget",  render:(v) => v ? functions.formatCurrency(v) : "—" },
    { key:"start_date", label:"Start",   render:(v) => functions.formatDate(v) },
    { key:"end_date",   label:"End",     render:(v) => functions.formatDate(v) },
    { key:"status",     label:"Status",  render:(v) => statusBadge(v) },
    { key:"id",         label:"Actions", render:(id, row) => (
      <div className="d-flex gap-1">
        <button className="btn btn-sm btn-outline-primary" onClick={() => openVA(row)} title="Budget vs Actual"><i className="fa-solid fa-chart-bar me-1"></i>B/A</button>
        <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/journal?project_id=${id}`)} title="View Transactions"><i className="fa-solid fa-list"></i></button>
        {row.status === "active" && <button className="btn btn-sm btn-outline-danger" onClick={() => handleClose(id)} title="Close"><i className="fa-solid fa-xmark"></i></button>}
      </div>
    )},
  ];

  return (
    <AppContainer
      title="Projects & Cost Centres"
      previousPageTitle="Accounting"
      description="Track income and expenses against specific projects, cost centres, departments, or grants. Assign transactions to projects when creating journal entries, invoices, or vouchers."
      actions={[
        { label:"New Project", icon:"fa-solid fa-plus", className:"btn btn-primary", onClick:() => setShowAdd(true) },
      ]}
    >
      <Toaster />

      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year (for Budget vs Actual)</label>
            <Select options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select year" isSearchable />
          </div>
        </div>
      </div>

      <div className="card custom-card p-3">
        <ExportBar data={projects}
          columns={[{key:"code",label:"Code"},{key:"name",label:"Name"},{key:"type",label:"Type"},{key:"budget",label:"Budget"},{key:"start_date",label:"Start"},{key:"end_date",label:"End"},{key:"status",label:"Status"}]}
          filename="projects" pdfTitle="Projects & Cost Centres"
          printCode="ACC-PROJECTS" exportCode="ACC-PROJECTS" />
        <DataTable columns={columns} data={projects} loading={loading} pageSize={15} emptyMessage="No projects found." />
      </div>

      {/* CREATE MODAL */}
      {showAdd && (
        <AppModal
          title="Create Project / Cost Centre"
          size="lg"
          onClose={() => setShowAdd(false)}
          onSubmit={handleCreate}
          footer={
            <>
              <button type="button" className="btn btn-light" onClick={() => setShowAdd(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Creating..." : "Create"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-3">
              <label className="form-label tx-12 fw-semibold">Code <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.code} onChange={(e) => setForm({...form,code:e.target.value})} placeholder="e.g. PROJ-001" required />
            </div>
            <div className="col-lg-5">
              <label className="form-label tx-12 fw-semibold">Name <span className="tx-danger">*</span></label>
              <input className="form-control" value={form.name} onChange={(e) => setForm({...form,name:e.target.value})} placeholder="e.g. Library Construction" required />
            </div>
            <div className="col-lg-4">
              <label className="form-label tx-12 fw-semibold">Type</label>
              <Select options={typeOptions} value={form.type} onChange={(v) => setForm({...form,type:v})} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Start Date</label>
              <input type="date" className="form-control" value={form.start_date} onChange={(e) => setForm({...form,start_date:e.target.value})} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">End Date</label>
              <input type="date" className="form-control" value={form.end_date} onChange={(e) => setForm({...form,end_date:e.target.value})} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Budget (UGX)</label>
              <input type="number" className="form-control" value={form.budget} onChange={(e) => setForm({...form,budget:e.target.value})} placeholder="0.00" />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Parent Project</label>
              <Select options={parentOptions} value={form.parent_id} onChange={(v) => setForm({...form,parent_id:v})} placeholder="No parent" isClearable isSearchable />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Description</label>
              <textarea className="form-control" rows={2} value={form.description} onChange={(e) => setForm({...form,description:e.target.value})} />
            </div>
          </div>
        </AppModal>
      )}

      {/* BUDGET VS ACTUAL MODAL */}
      {showVA && selected && (
        <AppModal
          title={<>Budget vs Actual — <span className="tx-primary">{selected.name}</span></>}
          size="xl"
          onClose={() => setShowVA(false)}
        >
          {vaLoading ? (
            <div className="text-center py-4"><i className="fa-solid fa-spinner fa-spin fa-2x tx-primary"></i></div>
          ) : !vaData ? (
            <div className="text-center py-4 tx-muted">No data available.</div>
          ) : (
            <>
              <div className="row g-2 mb-3">
                {[
                  { label:"Total Budget",   value: vaData.total_budget,   color:"#6366f1" },
                  { label:"Total Actual",   value: vaData.total_actual,   color:"#ef4444" },
                  { label:"Variance",       value: vaData.total_variance, color: (vaData.total_variance || 0) >= 0 ? "#10b981" : "#ef4444" },
                  { label:"% Used",         value: vaData.percentage_used, type:"percent" },
                ].map((c, i) => (
                  <div key={i} className="col-lg-3 col-sm-6">
                    <div className="card p-3" style={{ borderLeft:`3px solid ${c.color}` }}>
                      <div className="tx-11 tx-muted">{c.label}</div>
                      <div className="fw-bold tx-16" style={{ color: c.color }}>
                        {c.type === "percent" ? `${(c.value || 0).toFixed(1)}%` : functions.formatCurrency(c.value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="bg-light">
                    <tr><th>Account</th><th className="text-end">Budget</th><th className="text-end">Actual</th><th className="text-end">Variance</th><th className="text-end">% Used</th></tr>
                  </thead>
                  <tbody>
                    {(vaData.lines || []).map((line, i) => {
                      const variance = (line.budget_amount || 0) - (line.actual_amount || 0);
                      const pct      = line.budget_amount ? ((line.actual_amount / line.budget_amount) * 100).toFixed(1) : "—";
                      return (
                        <tr key={i}>
                          <td><code>{line.account_code}</code> {line.account_name}</td>
                          <td className="text-end">{functions.formatCurrency(line.budget_amount)}</td>
                          <td className="text-end">{functions.formatCurrency(line.actual_amount)}</td>
                          <td className={`text-end fw-bold ${variance >= 0 ? "tx-success" : "tx-danger"}`}>{functions.formatCurrency(Math.abs(variance))} {variance >= 0 ? "↑" : "↓"}</td>
                          <td className="text-end">
                            <div style={{ display:"flex", alignItems:"center", justifyContent:"flex-end", gap:"6px" }}>
                              <span>{pct}%</span>
                              <div style={{ width:"60px", height:"6px", background:"#e5e7eb", borderRadius:"3px", overflow:"hidden" }}>
                                <div style={{ width:`${Math.min(parseFloat(pct) || 0, 100)}%`, height:"100%", background: parseFloat(pct) > 90 ? "#ef4444" : "#10b981" }}></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <ExportBar data={vaData.lines || []}
                columns={[{key:"account_code",label:"Code"},{key:"account_name",label:"Account"},{key:"budget_amount",label:"Budget"},{key:"actual_amount",label:"Actual"}]}
                filename={`bva-${selected.code}`} pdfTitle={`Budget vs Actual — ${selected.name}`}
                printCode="ACC-PROJECTS" exportCode="ACC-PROJECTS" className="mt-3" />
            </>
          )}
        </AppModal>
      )}
    </AppContainer>
  );
};

export default Projects;
